import Swal from "sweetalert2";
import styled from 'styled-components';
import { CollectionCategoryEnum } from 'Enums';
import StacInterface from "utils/StacInterface";
import withReactContent from "sweetalert2-react-content";

const LinkSpan = styled.span`
  text-decoration: underline;

  &:hover{
    cursor:pointer;
    color:#007bff;
  }
`;

export const getOidcStorageKey = () => {
  const authSettings = JSON.parse(localStorage.getItem('authSettings'));
  if (authSettings) {
    return `oidc.user:${authSettings.auth_server}:${authSettings.client_id}`;
  }
  return null;
};

export const getOidcInfo = () => {
  const key = getOidcStorageKey();
  if (key) {
    const oidc = JSON.parse(localStorage.getItem(key));
    return oidc;
  }
  return null;
};

export const getToken = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    return oidc.id_token;
  }
  return null;
};

const getDay = (date) => {
  if (!date) return '';
  return `${new Date(date)?.getDate()}`;
};

const getValue = (price) => {
  if (Number.isNaN(price)) return 0;
  return Number(price).toFixed(2);
};

const getNameAndValueArr = history => history.map(day => ({
  name: getDay(day?.date),
  value: getValue(day.priceUsd),
}));

export const getWeekChartData = (cryptoHistory) => {
  if (!cryptoHistory) return null;

  const weekChartDataArr = cryptoHistory?.map(item => ({
    currency: item?.currency,
    history: getNameAndValueArr(item?.history.slice(7, item?.history.length)),
  }));

  return Object.fromEntries(weekChartDataArr.map(item => [item.currency, item.history]));
};

export const getShortCryptoAddress = value => `${value.slice(0, 5)}...${value.slice(-3)}`;

export const getLayerCollectionDetail = (layer) => {
  let collection_name = layer.split(':')[0];
  let collection_index = window.demonstrable_collections.findIndex(collection => collection_name.startsWith(collection.name));
  return [collection_index, window.demonstrable_collections[collection_index]];
}

export const radiansToDegrees = (radians) => Math.round(radians * (180 / Math.PI) * 100) / 100;

export const getThumbnail = (id) => {
  StacInterface.instance.getThumbnail(id)
    .then((response) => {
      const url = URL.createObjectURL(response.data);
      var link = document.createElement("a");
      link.href = url;
      link.target = "_blank"
      link.click();
      URL.revokeObjectURL(url);
      link.remove();
    })
}

export const getItemSTAC = (id) => {
  StacInterface.instance.getItem(id).then((response) => {
    const blob = new Blob([JSON.stringify(response.data, null, "\t")]);
    const url = URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = url;
    link.target = "_blank"
    link.click();
    URL.revokeObjectURL(url);
    link.remove();
  })
}

export const copyToClipboard = (string) => {
  navigator.clipboard.writeText(string).then(() => {
    withReactContent(Swal).fire({
      title: "Success",
      html: `URL is copied to your clipboard:\n${string}`,
    });
  }).catch(() => {
    withReactContent(Swal).fire({
      title: "Error",
      html: "Something went wrong in copying to clipboard.",
    });
  });
}

export const createMetadata = (item_STAC) => {
  if (item_STAC === undefined) return;
  
  var licenseLink;
  var constellationLink;
  var platformLink;
  var epsgLink;
  var instrumentsLink;
  item_STAC.links?.map(link => {
    if (link?.rel === "license") {
      licenseLink = link.href;
    } else if (link?.rel === "constellation") {
      constellationLink = link.href;
    } else if (link?.rel === "platform") {
      platformLink = link.href;
    } else if (link?.rel === "epsg") {
      epsgLink = link.href;
    } else if (link?.rel === "instruments") {
      instrumentsLink = link.href;
    }
  })

  var licensorName;
  var licensorLink;

  item_STAC.providers?.map(provider => {
    if (provider.roles.includes("licensor")) {
      licensorName = provider.name;
      licensorLink = provider.url
    }
  })

  var wmsUrl;
  var wmtsUrl;
  var tmsUrl;

  item_STAC.links?.map(link => {
    if (link.rel === "wms") {
      wmsUrl = link.href + '?LAYERS=' + link.title
    }
    else if (link.rel === "wmts") {
      wmtsUrl = link.href
    } else if (link.rel === "xyz") {
      tmsUrl = link.href
    }
  })

  var collection = item_STAC.collection;
  let [_, collectionObject] = getLayerCollectionDetail(`${collection}:mission`);

  var info = {
    Collection: {
      value: collection
    },
    Licensor: {
      value: <a className="underline" href={licensorLink} target="_blank">{licensorName}</a>
    },
    License: {
      value: <a className="underline" href={licenseLink} target="_blank" > {item_STAC.properties.license}</a>
    },
    Acquired: {
      value: item_STAC.properties.end_datetime.slice(0, item_STAC.properties.end_datetime.indexOf('T'))
    },
    Created: {
      value: item_STAC.properties?.created?.replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '')
    },
    Updated: {
      value: item_STAC.properties?.updated?.replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '')
    },
    GSD: {
      value: item_STAC.properties["eo:gsd"]?.toFixed(3)
    },
    Projection: {
      value: <a className="underline" href={epsgLink} target="_blank">{item_STAC.properties["prj:epsg"]}</a>
    },
    Omega: {
      value: item_STAC.properties["pers:omega"] ? radiansToDegrees(item_STAC.properties["pers:omega"]) : '-'
    },
    Phi: {
      value: item_STAC.properties["pers:phi"] ? radiansToDegrees(item_STAC.properties["pers:phi"]) : '-'
    },
    Kappa: {
      value: item_STAC.properties["pers:kappa"] ? radiansToDegrees(item_STAC.properties["pers:kappa"]) : '-'
    },
    'Processing Level': {
      value: item_STAC.properties["processing:level"]
    },
    'Elevation Model': {
      value: item_STAC.properties["elevation_model"]
    },
    'XY Accuracy': {
      value: item_STAC.properties["card4l:northern_geometric_accuracy"]?.toFixed(3)
    },
    Platform: {
      value: <a className="underline" href={platformLink} target="_blank">{item_STAC.properties.platform}</a>
    },
    Instrument: {
      value: <a className="underline" href={instrumentsLink} target="_blank">{item_STAC.properties.instruments}</a>
    },
    'Web Map Services': {
      value: (
        <>
          <LinkSpan onClick={() => copyToClipboard(wmsUrl)}>WMS</LinkSpan> / <LinkSpan onClick={() => copyToClipboard(wmtsUrl)}>WMTS</LinkSpan> / <LinkSpan onClick={() => copyToClipboard(tmsUrl)}>TMS</LinkSpan>
        </>
      )
    },
    Assets: {
      value: (
        <>
          <LinkSpan onClick={() => getItemSTAC(item_STAC.id)}>JSON</LinkSpan> / <LinkSpan onClick={() => getThumbnail(item_STAC.id)} >Thumbnail</LinkSpan>
        </>
      )
    }
  };

  if (collectionObject.category === CollectionCategoryEnum.terrain_mosaic || collectionObject.category === CollectionCategoryEnum.terrain_tileset) {
    delete info['XY Accuracy']
  } else {
    delete info['Elevation Model']
  }

  if (collectionObject.category !== CollectionCategoryEnum.oblique_photo_mosaic && collectionObject.category !== CollectionCategoryEnum.oblique_photo_tileset) {
    delete info['Omega']
    delete info['Phi']
    delete info['Kappa']
  }

  if (info['Updated'].value === info['Created'].value) {
    info['Updated'] = '-';
  }

  return info;
}